import React from 'react';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {prism} from 'react-syntax-highlighter/dist/esm/styles/prism';

const Chat = ({ onSectionChange }) => {
    return (
        <div className="api-reference-layout">
            <div className="api-content">
                <div className="api-header">
                    <div className="api-method-badge post">POST</div>
                    <h1>chat - Memory-Enhanced Chat</h1>
                    <p className="api-description">
                        Send chat messages and receive AI responses enhanced with user memory context
                    </p>
                </div>

                <div className="api-endpoint">
                    <div className="endpoint-url">
                        <span className="method post">POST</span>
                        <span className="url">/api/v2/chat</span>
                    </div>
                </div>

                <div className="api-section">
                    <h2 id="authorization">Authorization</h2>
                    <div className="auth-item">
                        <div className="auth-header">
                            <span className="auth-name">Authorization</span>
                            <span className="auth-type">string</span>
                            <span className="auth-location">header</span>
                            <span className="required-badge">required</span>
                        </div>
                        <p>Bearer authentication header of the form Bearer &lt;token&gt;, where &lt;token&gt; is your API key.</p>
                    </div>
                </div>

                <div className="api-section">
                    <h2 id="request-body">Request Body</h2>
                    <div className="params-container">
                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">user_id</span>
                                <span className="param-type">string</span>
                                <span className="required-badge">required</span>
                            </div>
                            <p>Unique identifier for the user</p>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>"user001"</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">user_name</span>
                                <span className="param-type">string</span>
                                <span className="optional-badge">optional</span>
                            </div>
                            <p>Display name for the user</p>
                            <div className="param-default">
                                <strong>Default:</strong>
                                <code>null</code>
                            </div>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>"Alice Johnson"</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">agent_id</span>
                                <span className="param-type">string</span>
                                <span className="required-badge">required</span>
                            </div>
                            <p>Unique identifier for the agent</p>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>"gpt001"</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">agent_name</span>
                                <span className="param-type">string</span>
                                <span className="optional-badge">optional</span>
                            </div>
                            <p>Display name for the agent</p>
                            <div className="param-default">
                                <strong>Default:</strong>
                                <code>null</code>
                            </div>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>"AI Assistant"</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">message</span>
                                <span className="param-type">string</span>
                                <span className="required-badge">required</span>
                            </div>
                            <p>User message content</p>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>"What movies do you think I'd enjoy based on my preferences?"</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">max_context_tokens</span>
                                <span className="param-type">integer</span>
                                <span className="optional-badge">optional</span>
                            </div>
                            <p>Maximum number of context tokens to use</p>
                            <div className="param-default">
                                <strong>Default:</strong>
                                <code>null</code>
                            </div>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>4000</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">kwargs</span>
                                <span className="param-type">object</span>
                                <span className="optional-badge">optional</span>
                            </div>
                            <p>Additional LLM parameters (e.g., temperature, max_tokens, etc.)</p>
                            <div className="param-default">
                                <strong>Default:</strong>
                                <code>{}</code>
                            </div>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>{`{"temperature": 0.7, "max_tokens": 150, "model": "gpt-4o"}`}</code>
                            </div>
                            <div className="param-supported-models">
                                <strong>Supported LLM Models:</strong>
                                <div className="models-grid">
                                    <div className="model-category">
                                        <span className="category-title">GPT Models:</span>
                                        <code>"gpt-5"</code>, <code>"gpt-5-nano"</code>, <code>"gpt-5-mini"</code>, <code>"gpt-4.1"</code>, <code>"gpt-4.1-mini"</code>, <code>"gpt-4o"</code>, <code>"gpt-4o-mini"</code>
                                    </div>
                                    <div className="model-category">
                                        <span className="category-title">Gemini Models:</span>
                                        <code>"gemini-2.5-pro"</code>, <code>"gemini-2.5-flash"</code>
                                    </div>
                                    <div className="model-category">
                                        <span className="category-title">Claude Models:</span>
                                        <code>"claude-opus-4.1"</code>, <code>"claude-sonnet-4"</code>
                                    </div>
                                    <div className="model-category">
                                        <span className="category-title">Grok Models:</span>
                                        <code>"grok-4"</code>
                                    </div>
                                    <div className="model-category">
                                        <span className="category-title">DeepSeek Models:</span>
                                        <code>"deepseek-R1"</code>, <code>"deepseek-v3.1"</code>, <code>"deepseek-v3.1-thinking"</code>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="api-section">
                    <h2 id="response">Response</h2>
                    <div className="response-status">
                        <span className="status-code success">200</span>
                        <span className="content-type">application/json</span>
                    </div>

                    <div className="params-container">
                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">message</span>
                                <span className="param-type">string</span>
                            </div>
                            <p>AI response message</p>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">chat_token_usage</span>
                                <span className="param-type">ChatTokenUsage</span>
                            </div>
                            <p>Token usage statistics</p>
                        </div>
                    </div>

                    <div className="response-schema">
                        <h3>ChatTokenUsage Structure:</h3>
                        <div className="params-container">
                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">prompt_tokens</span>
                                    <span className="param-type">integer</span>
                                </div>
                                <p>Number of tokens used in the prompt</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">completion_tokens</span>
                                    <span className="param-type">integer</span>
                                </div>
                                <p>Number of tokens used in the completion</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">total_tokens</span>
                                    <span className="param-type">integer</span>
                                </div>
                                <p>Total number of tokens used</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">prompt_tokens_breakdown</span>
                                    <span className="param-type">ChatTokenUsageBreakdown</span>
                                </div>
                                <p>Detailed breakdown of prompt token usage</p>
                            </div>
                        </div>

                        <h3>ChatTokenUsageBreakdown Structure:</h3>
                        <div className="params-container">
                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">current_query</span>
                                    <span className="param-type">integer</span>
                                </div>
                                <p>Tokens used by the current query</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">short_term_context</span>
                                    <span className="param-type">integer</span>
                                </div>
                                <p>Tokens used by short-term context</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">user_profile</span>
                                    <span className="param-type">integer</span>
                                </div>
                                <p>Tokens used by user profile information</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">retrieved_memory</span>
                                    <span className="param-type">integer</span>
                                </div>
                                <p>Tokens used by retrieved memory content</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="api-demo-sidebar">
                <div className="demo-sticky">
                    <div className="demo-header">
                        <div className="demo-tabs">
                            <button className="demo-tab active">Python</button>
                            <button className="demo-tab">JavaScript</button>
                            <button className="demo-tab">cURL</button>
                        </div>
                    </div>

                    <div className="demo-content">
                        <h3 id="examples">Request Example</h3>
                        <SyntaxHighlighter
                            language="python"
                            style={prism}
                            customStyle={{
                                margin: 0,
                                padding: '20px',
                                background: 'white',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                borderRadius: '0 0 8px 8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            {`import os
from memu import MemuClient

# Initialize MemU client
memu_client = MemuClient(
    base_url="https://api.memu.so",
    api_key=os.getenv("MEMU_API_KEY")
)

# Basic chat with memory enhancement
response = memu_client.chat(
    user_id="user001",
    user_name="Alice Johnson",
    agent_id="gpt001",
    agent_name="AI Assistant",
    message="What movies do you think I'd enjoy based on my preferences?"
)

print(f"AI Response: {response.message}")
print(f"Total tokens used: {response.chat_token_usage.total_tokens}")

# Chat with custom LLM parameters
custom_response = memu_client.chat(
    user_id="user001",
    agent_id="gpt001",
    message="Tell me about my favorite hobbies",
    max_context_tokens=3000,
    kwargs={
        "temperature": 0.8,
        "max_tokens": 200,
        "top_p": 0.9
    }
)

print(f"Custom Response: {custom_response.message}")

# Check token usage breakdown
usage = custom_response.chat_token_usage
breakdown = usage.prompt_tokens_breakdown

print(f"Prompt tokens breakdown:")
print(f"  Current query: {breakdown.current_query}")
print(f"  Short-term context: {breakdown.short_term_context}")
print(f"  User profile: {breakdown.user_profile}")
print(f"  Retrieved memory: {breakdown.retrieved_memory}")
print(f"  Total prompt tokens: {usage.prompt_tokens}")
print(f"  Completion tokens: {usage.completion_tokens}")`}
                        </SyntaxHighlighter>
                    </div>

                    <div className="response-section">
                        <div className="response-header">
                            <span className="response-status-code">200</span>
                            <span>Success Response</span>
                        </div>
                        <SyntaxHighlighter
                            language="json"
                            style={prism}
                            customStyle={{
                                margin: 0,
                                padding: '20px',
                                background: 'white',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                borderRadius: '0 0 8px 8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            {`{
  "message": "Based on your preferences for sci-fi and thriller movies with complex plots, I'd recommend 'Inception' and 'Blade Runner 2049'. You mentioned enjoying films with strong character development, so 'Arrival' would also be perfect for you. These all feature the intricate storytelling and thought-provoking themes you love.",
  "chat_token_usage": {
    "prompt_tokens": 1250,
    "completion_tokens": 85,
    "total_tokens": 1335,
    "prompt_tokens_breakdown": {
      "current_query": 45,
      "short_term_context": 120,
      "user_profile": 380,
      "retrieved_memory": 705
    }
  }
}`}
                        </SyntaxHighlighter>
                    </div>

                    <div className="response-section">
                        <div className="response-header">
                            <span className="response-status-code">200</span>
                            <span>Response with Custom Parameters</span>
                        </div>
                        <SyntaxHighlighter
                            language="json"
                            style={prism}
                            customStyle={{
                                margin: 0,
                                padding: '20px',
                                background: 'white',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                borderRadius: '0 0 8px 8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            {`{
  "message": "Your favorite hobbies include hiking in the Rocky Mountains, which you do every summer, and rock climbing on weekends. You also enjoy photography during your outdoor adventures and have mentioned loving the peaceful moments you find in nature. These activities seem to be your way of staying active while connecting with the outdoors.",
  "chat_token_usage": {
    "prompt_tokens": 890,
    "completion_tokens": 120,
    "total_tokens": 1010,
    "prompt_tokens_breakdown": {
      "current_query": 35,
      "short_term_context": 85,
      "user_profile": 290,
      "retrieved_memory": 480
    }
  }
}`}
                        </SyntaxHighlighter>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default Chat;

